/* eslint-disable indent */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import values from 'lodash/values'
import times from 'lodash/times'

import { IQuestion, QuestionType } from 'cat-shared/utils'
import { Form } from 'client/semantic-imports'
import { AppButton, AppDivider, AppGroup } from 'client/ui-building-blocks'
import { QuestionSubmitFormValues } from 'client/utils'
import { useAppContext, useModalDialogContext, usePageStateContext } from 'client/hooks'

import {
  getSubmitQuestionInitialValues,
  isAccountConfirmed,
  getAccountStatus,
} from 'client/services'

import AnswersListItem from '../QuestionAnswersListItem/question-answers-list-item'

type Props = {
  question: IQuestion
  previewMode?: boolean
  isBeingSubmitted?: boolean
  handleQuestionSubmission?: (formValues: QuestionSubmitFormValues) => void
}

export const QuestionSubmitForm = ({
  question = null,
  previewMode = false,
  isBeingSubmitted = false,
  handleQuestionSubmission,
}: Props) => {
  const { t } = useTranslation()

  const { authUser } = useAppContext()
  const { isSubmitQuestionFormDirty, setIsSubmitQuestionFormDirty } =
    usePageStateContext()
  const { toggleNoAccessModal } = useModalDialogContext()

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      votes: {},
    },
  })

  useEffect(() => {
    if (isDirty !== isSubmitQuestionFormDirty) {
      setIsSubmitQuestionFormDirty(isDirty)
    }
    return () => {
      setIsSubmitQuestionFormDirty(false)
    }
  }, [isDirty])

  useEffect(() => {
    const values = getSubmitQuestionInitialValues(question)
    reset(values)
  }, [question._id])

  // onSubmit handler
  const onSubmit = async (formValues: QuestionSubmitFormValues): Promise<unknown> => {
    if (isAccountConfirmed(getAccountStatus(authUser))) {
      if (!values(formValues.votes).some(selected => selected)) {
        return
      }
      handleQuestionSubmission(formValues)
    } else {
      toggleNoAccessModal(true)
    }
  }

  const onResetClick = () => {
    reset()
  }

  const { type, options } = question

  const votes = watch('votes')
  const selectedAnswersCount = values(votes).filter(selected => selected).length

  return (
    <Form
      onSubmit={handleSubmit(data => {
        return onSubmit(data)
      })}
    >
      <Controller
        control={control}
        name="votes"
        rules={{
          required: t('common.validation.required'),
        }}
        render={({ field: { value: votes, onChange } }) => {
          return (
            <>
              {options.map((answer, i) => {
                const onClick = () => {
                  // selecting not selected
                  if (!votes[i]) {
                    if (type === QuestionType.SingleChoice) {
                      times(question.options.length, i => {
                        votes[i] = false
                      })
                    }
                    votes[i] = true

                    // deselecting
                  } else {
                    votes[i] = false
                  }
                  onChange({ target: { value: votes } })
                }

                return (
                  <AnswersListItem
                    key={question._id?.toString() + '_' + i}
                    last={i === options.length - 1}
                    disabled={isBeingSubmitted}
                    marked={Boolean(votes[i])}
                    editMode
                    highlightOnHover
                    content={answer}
                    checkBox={{
                      value: i,
                      checked: Boolean(votes[i]),
                    }}
                    onClick={onClick}
                  />
                )
              })}
            </>
          )
        }}
      />
      {!previewMode && (
        <>
          <AppDivider size="small" />
          <AppGroup fullWidth="mobile" position="center">
            <AppButton
              variant="info"
              text={t('common.buttons.answer')}
              disabled={!isValid || !isDirty || selectedAnswersCount === 0}
              loading={isBeingSubmitted}
              type="submit"
            />
            <AppButton
              variant="blank"
              text={t('common.buttons.reset')}
              disabled={
                isBeingSubmitted || !isValid || !isDirty || selectedAnswersCount === 0
              }
              onClick={onResetClick}
              type="reset"
              id="submit-form-reset-btn"
            />
          </AppGroup>
          <AppDivider size="large" />
        </>
      )}
    </Form>
  )
}
